import React from 'react';
import { Bell } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { useToasts } from '../hooks/useToasts';
import type { AppNotification } from '../types/notifications';
import { AppToastViewport } from './AppToastViewport';
import { NotificationsPanel } from './NotificationsPanel';

type Props = {
  userId: string | null;
  instanceId: string | null;
};

export function NotificationsBell({ userId, instanceId }: Props) {
  const { notifications, unreadCount, refresh, markRead, archive, unarchive } = useNotifications(userId, instanceId);
  const { toasts, pushToast, dismissToast } = useToasts();

  const handleArchive = React.useCallback(
    (n: AppNotification) => {
      void archive(n).then((ok) => {
        if (!ok) {
          pushToast('Benachrichtigung konnte nicht archiviert werden.', 'error');
          return;
        }
        pushToast('Archiviert', 'info', { label: 'Rückgängig', onClick: () => void unarchive(n) });
      });
    },
    [archive, unarchive, pushToast],
  );
  const [open, setOpen] = React.useState(false);
  const [panelFixed, setPanelFixed] = React.useState<{ top: number; right: number } | null>(null);
  const bellRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!open) {
      setPanelFixed(null);
      return;
    }
    const updatePos = () => {
      const btn = bellRef.current?.querySelector('button');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      setPanelFixed({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (bellRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) void refresh();
      return next;
    });
  };

  return (
    <>
      <div className="relative" ref={bellRef}>
        <button
          type="button"
          onClick={toggle}
          className="relative w-8 h-8 rounded-full flex items-center justify-center text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted transition-colors"
          title="Benachrichtigungen"
          aria-label="Benachrichtigungen"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 ? (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-pipedrive-blue text-white text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </button>
      </div>
      <NotificationsPanel
        open={open}
        panelFixed={panelFixed}
        notifications={notifications}
        unreadCount={unreadCount}
        onClose={() => setOpen(false)}
        onMarkRead={markRead}
        onArchive={handleArchive}
        panelRef={panelRef}
      />
      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </>
  );
}
